import React, { useEffect, useState, useCallback } from "react";
import { debounce } from "lodash";
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  CardHeader, 
  IconButton, 
  Input, 
  InputAdornment, 
  Tooltip, 
  Dialog, 
  DialogActions, 
  DialogTitle, 
  FormGroup, 
  FormControlLabel, 
  Switch, 
  Typography,
  Grid,
  Chip,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Paper,
  Container,
  Fade,
  Zoom,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import SportsIcon from "@mui/icons-material/Sports";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { LeagueManagementService } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import TableFilter from "../../components/shared/TableFilter";
import { STATUS_OPTIONS_LIST } from "../../utils/constants";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EditOutlined } from "@mui/icons-material";
import { Loader } from "../../components/CustomLoader";
import { fDateOnly } from "../../utils/formatTime";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { ADMIN_ROLES } from "../../utils/constants";
import "./LeagueManagement.css";

export default function LeagueManagement() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const user = useSelector((state) => state.auth.user);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState([]);
  const [show, setShow] = useState(false);
  const [warning, setWarning] = useState("");
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusId, setStatusId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [country, setCountry] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [totalActiveLeagues, setTotalActiveLeagues] = useState(0);
  const [totalInactiveLeagues, setTotalInactiveLeagues] = useState(0);
  const [allLeagues, setAllLeagues] = useState([]);


  const fetchAllLeaguesForStats = async () => {
    try {
      const body = {
        searchItem: "",
        pageNumber: 1,
        pageSize: 1000, // Get all leagues for stats
        status: [],
        countryId: [],
      };
      const res = await LeagueManagementService.getBids(body);
      if (res.status === 200 && res?.data?.data?.leagues) {
        const allLeaguesData = res.data.data.leagues;
        setAllLeagues(allLeaguesData);
        
        // Calculate total stats from all leagues
        setTotalActiveLeagues(allLeaguesData.filter(item => item.status === 1).length);
        setTotalInactiveLeagues(allLeaguesData.filter(item => item.status === 0).length);
      }
    } catch (err) {
      console.error('Error fetching all leagues for stats:', err);
    }
  };

  const fetchBidPackage = async (query) => {
    const body = {
      searchItem: query || "",
      pageNumber: currentPage,
      pageSize: pageSize,
      status: status,
      countryId: selectedCountry,
    };

    try {
      setIsLoading(true);
      setLoaderDelay(false);
      const res = await LeagueManagementService.getBids(body);
      if (res.status === 200) {
        setTimeout(() => {
          setData(res?.data?.data?.leagues);
          setTotalPages(res?.data?.data?.totalRecords);
          setLoaderDelay(true);
        }, 500);
      }
    } catch (err) {
      setData(err?.response?.data?.message?.data);
      errorHandler(err);
      setLoaderDelay(true);
    } finally {
      setIsLoading(false)
    }
  };

  const fetchCountry = async () => {
    try {
      const response = await LeagueManagementService.getCountry();

      if (response.status == 200) {
        setCountry(response?.data?.data || [])
      }

    } catch (error) {
      toast.error(error?.response?.data?.message || "Error in fetching Country.")
    }
  }
     
  // Initialize currentPage and viewMode from URL parameters
  useEffect(() => {
    const pageFromUrl = searchParams.get('page');
    const viewModeFromUrl = searchParams.get('view');
    
    if (pageFromUrl && !isNaN(pageFromUrl) && parseInt(pageFromUrl) > 0) {
      setCurrentPage(parseInt(pageFromUrl));
    }
    
    if (viewModeFromUrl && (viewModeFromUrl === 'grid' || viewModeFromUrl === 'list')) {
      setViewMode(viewModeFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchCountry();
    fetchAllLeaguesForStats(); // Fetch all leagues for stats on component mount
  }, [])

  const debouncedSearch = useCallback(
    debounce((query) => fetchBidPackage(query), 400),
    [currentPage, pageSize, status]
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    debouncedSearch(query);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Update URL parameter to maintain page state
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', page);
    setSearchParams(newSearchParams);
  };


  const deleteData = async (id) => {

    Swal.fire({
      text: "Are you sure you want to delete this league?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await LeagueManagementService.deleteBid({ id });
          if (res?.status === 200) {
            toast.success(res?.data?.message, { toastId: "success" });
            setData((prevData) => prevData.filter((item) => item._id !== id));
            setTotalPages((prevTotal) => prevTotal - 1);
            setRefresh(!refresh);
          } else {
            toast.error(res?.data?.message, { toastId: "error" });
          }
        } catch (error) {
          errorHandler(error);
        }
      }
    });
  };

  const toggleChecked = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const res = await LeagueManagementService.changeStatus({
        id,
        status: newStatus,
      });

      if (res.status === 200) {
        toast.success(res?.data?.message, { toastId: "success" });
        setRefresh(!refresh);
      } else {
        toast.error(res?.data?.message, { toastId: "error" });
      }
    } catch (err) {
      errorHandler(err);
    }
  };

  useEffect(() => {
    fetchBidPackage(searchItem);
  }, [currentPage, pageSize, status, refresh, selectedCountry]);

  const renderLeagueCard = (league, index) => (
    <Zoom in={!isLoading} className={`league-card-zoom league-card-zoom-${index}`}>
      <Grid item xs={12} sm={6} md={4} lg={3} key={league._id}>
        <Card className="league-card">
          <Box className="league-card-index-badge">
            {(currentPage - 1) * pageSize + index + 1}
          </Box>

          <CardContent className="league-card-content">
            <Box className="league-card-header">
              <Avatar className="league-card-avatar">
                <SportsIcon />
              </Avatar>
              <Box className="league-card-title-container">
                <Typography variant="h6" className="league-card-name" sx={{ 
                  color: 'white', 
                  fontWeight: 600, 
                  mb: 0.5,
                  fontSize: '1.1rem',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                }}>
                  {league.name || 'No Name'}
                </Typography>
                
                <Typography variant="body2" className="league-card-country" sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)', 
                  mb: 1,
                  fontSize: '0.9rem'
                }}>
                  {league.country || 'N/A'}
                </Typography>
                
                <Chip
                  label={league.status === 1 ? 'Active' : 'Inactive'}
                  size="small"
                  className="league-card-status-chip"
                  sx={{
                    backgroundColor: league.status === 1 
                      ? 'rgba(76, 175, 80, 0.2)' 
                      : 'rgba(244, 67, 54, 0.2)',
                    color: league.status === 1 
                      ? '#4caf50' 
                      : '#f44336',
                    fontWeight: 600,
                    border: '1px solid',
                    borderColor: league.status === 1 
                      ? 'rgba(76, 175, 80, 0.3)' 
                      : 'rgba(244, 67, 54, 0.3)'
                  }}
                />
              </Box>
            </Box>

            <Divider className="league-card-divider" />

            {/* League Details */}
            <Box className="league-card-details-compact">
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Box className="league-card-detail-item-compact">
                    <Typography variant="caption" className="league-card-detail-label-compact">
                      League ID
                    </Typography>
                    <Typography variant="body2" className="league-card-detail-value-compact">
                      {league.leagueId || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={6}>
                  <Box className="league-card-detail-item-compact">
                    <Typography variant="caption" className="league-card-detail-label-compact">
                      Season ID
                    </Typography>
                    <Typography variant="body2" className="league-card-detail-value-compact">
                      {league.currentSeason?.seasonId || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box className="league-card-detail-item-compact">
                    <Typography variant="caption" className="league-card-detail-label-compact">
                      Country
                    </Typography>
                    <Typography variant="body2" className="league-card-detail-value-compact">
                      {league.country || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={6}>
                  <Box className="league-card-detail-item-compact">
                    <Typography variant="caption" className="league-card-detail-label-compact">
                      Starting At
                    </Typography>
                    <Typography variant="body2" className="league-card-detail-value-compact">
                      {league.currentSeason?.startingAt ? fDateOnly(league.currentSeason.startingAt) : 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box className="league-card-detail-item-compact">
                    <Typography variant="caption" className="league-card-detail-label-compact">
                      Ending At
                    </Typography>
                    <Typography variant="body2" className="league-card-detail-value-compact">
                      {league.currentSeason?.endingAt ? fDateOnly(league.currentSeason.endingAt) : 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Divider className="league-card-divider" />

            <Box className="league-card-actions">
              <Tooltip title="View">
                <IconButton
                  size="small"
                  className="league-action-button-view"
                  onClick={() => {
                    navigate(`/league-management/view/${league._id}`);
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* <Tooltip title="Edit">
                <IconButton
                  size="small"
                  className="league-action-button-edit"
                  onClick={() => navigate(`/league-management/edit/${league._id}?page=${currentPage}&view=${viewMode}`)}
                >
                  <EditOutlined fontSize="small" />
                </IconButton>
              </Tooltip> */}
            </Box>

            {user?.role !== ADMIN_ROLES.JUNIOR && (
              <Box className="league-card-status-container">
                <Typography variant="caption" className="league-card-status-text">
                  {league.status === 1 ? '🟢 Active' : '🔴 Inactive'}
                </Typography>
                <Switch
                  checked={league.status === 1}
                  onChange={() => {
                    setShow(true);
                    setCurrentStatus(league.status);
                    setStatusId(league._id);
                    setWarning(
                      `Are you sure you want to ${league.status === 1 ? "deactivate" : "activate"} this league?`
                    );
                  }}
                  className="league-card-status-switch"
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Zoom>
  );

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "100px"
    },
    {
      name: "League Name",
      sortable: true,
      cell: (row) => {
        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
              {row.name || 'No Name'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              {row.country || 'N/A'}
            </Typography>
          </Box>
        );
      },
    },
    {
      name: "League ID",
      sortable: true,
      selector: (row) => row.leagueId || '-',
    },
    {
      name: "Season ID",
      sortable: true,
      selector: (row) => row.currentSeason?.seasonId || '-',
    },
    {
      name: "Starting At",
      sortable: true,
      selector: (row) => row.currentSeason?.startingAt ? fDateOnly(row.currentSeason.startingAt) : '-',
    },
    {
      name: "Ending At",
      sortable: true,
      selector: (row) => row.currentSeason?.endingAt ? fDateOnly(row.currentSeason.endingAt) : '-',
    },
    {
      name: "Status",
      cell: (row) => (
        <Chip
          label={row.status === 1 ? 'Active' : 'Inactive'}
          size="small"
          sx={{
            backgroundColor: row.status === 1 
              ? 'rgba(76, 175, 80, 0.2)' 
              : 'rgba(244, 67, 54, 0.2)',
            color: row.status === 1 
              ? '#4caf50' 
              : '#f44336',
            fontWeight: 600,
            border: '1px solid',
            borderColor: row.status === 1 
              ? 'rgba(76, 175, 80, 0.3)' 
              : 'rgba(244, 67, 54, 0.3)'
          }}
        />
      ),
    },
    {
      name: "Action",
      center: true,
      cell: (row) => (
        <>
          <Tooltip title="View" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/league-management/view/${row?._id}`);
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          {/* <Tooltip title="Edit" placement="left">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/league-management/edit/${row?._id}?page=${currentPage}&view=${viewMode}`);
              }}
            >
              <EditOutlined />
            </IconButton>
          </Tooltip> */}
        </>
      ),
    },
  ];

  return (
    <Box className="league-management-container">
      <Helmet>
        <title>League Management</title>
      </Helmet>
      <Fade in timeout={500}>
        <Stack spacing={4}>
          {/* Header Section */}
          <Paper
            elevation={0}
            className="league-header-paper"
          >
            <Box className="league-header-content">
              <Box className="league-header-left">
                <Avatar className="league-header-avatar">
                  <SportsIcon className="league-header-icon" />
                </Avatar>
                <Box>
                  <Typography variant="h4" className="league-header-title">
                    League Management
                  </Typography>
                  <Typography variant="body1" className="league-header-subtitle">
                    Manage and monitor all leagues in your system
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={3} className="league-stats-grid">
              <Grid item xs={12} sm={6} md={3}>
                <Paper className="league-stat-card">
                  <Typography variant="h4" className="league-stat-number">
                    {allLeagues?.length || 0}
                  </Typography>
                  <Typography variant="body2" className="league-stat-label">
                    Total Leagues
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper className="league-stat-card-active">
                  <Typography variant="h4" className="league-stat-number-active">
                    {totalActiveLeagues}
                  </Typography>
                  <Typography variant="body2" className="league-stat-label">
                    Active Leagues
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper className="league-stat-card-inactive">
                  <Typography variant="h4" className="league-stat-number-inactive">
                    {totalInactiveLeagues}
                  </Typography>
                  <Typography variant="body2" className="league-stat-label">
                    Inactive Leagues
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper className="league-stat-card">
                  <Typography variant="h4" className="league-stat-number">
                    {country?.length || 0}
                  </Typography>
                  <Typography variant="body2" className="league-stat-label">
                    Countries
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>

          {/* Controls Section */}
          <Paper
            elevation={0}
            className="league-controls-paper"
          >
            <Box className="league-controls-content">
              <Box className="league-controls-left">
        <TableFilter
          data={country.map(c => ({ value: c.countryId, label: c.country }))}
          value={selectedCountry}
          setValue={setSelectedCountry}
          filterName="Country"
        />

        <TableFilter
          data={STATUS_OPTIONS_LIST}
          value={status}
          setValue={setStatus}
          filterName="Status"
        />

        <Input
          value={searchItem}
                  placeholder="Search leagues..."
          onChange={handleSearchChange}
                  className="league-search-input"
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="clear-search"
                onClick={() => {
                  const cleared = "";
                  setSearchItem(cleared);
                  debouncedSearch(cleared);
                }}
                        className="league-clear-search-button"
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        />
      </Box>

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode) {
                    setViewMode(newMode);
                    // Update URL parameter to maintain view mode
                    const newSearchParams = new URLSearchParams(searchParams);
                    newSearchParams.set('view', newMode);
                    setSearchParams(newSearchParams);
                  }
                }}
                className="league-view-toggle"
              >
                <ToggleButton value="grid">
                  <ViewModuleIcon />
                </ToggleButton>
                <ToggleButton value="list">
                  <ViewListIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Paper>

          {/* Content Section */}
          <Paper
            elevation={0}
            className="league-content-paper"
          >
            {viewMode === 'grid' ? (
              /* Grid View */
              <Box className="league-grid-container">
                {isLoading || !loaderDelay ? (
                  <Box className="league-loading-container">
                    <Loader />
                  </Box>
                ) : !data || data.length === 0 ? (
                  <Box className="league-empty-container">
                    <Typography variant="h6" className="league-empty-title">
                      No leagues found
                    </Typography>
                    <Typography variant="body2" className="league-empty-subtitle">
                      Try adjusting your search criteria or create a new league
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Grid container spacing={3} className="league-grid">
                      {data.map((league, index) => renderLeagueCard(league, index))}
                    </Grid>
                    
                    {/* Pagination for Grid View */}
                    <Box className="league-pagination-container">
                      <Box className="league-pagination-info">
                        <Typography variant="body2" className="league-pagination-text">
                          Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalPages)} of {totalPages} leagues
                        </Typography>
                      </Box>
                      <Box className="league-pagination-controls">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ color: 'white', whiteSpace: 'nowrap' }}>
                            Items per page:
                          </Typography>
                          <FormControl size="small" className="league-page-size-select">
                            <Select
                              value={pageSize}
                              onChange={(e) => setPageSize(e.target.value)}
                              displayEmpty
                              sx={{
                                color: 'white',
                                minWidth: 80,
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'rgba(255, 255, 255, 0.3)',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'rgba(255, 255, 255, 0.5)',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'rgba(255, 255, 255, 0.7)',
                                },
                                '& .MuiSvgIcon-root': {
                                  color: 'white',
                                },
                              }}
                            >
                              <MenuItem value={10}>10</MenuItem>
                              <MenuItem value={20}>20</MenuItem>
                              <MenuItem value={50}>50</MenuItem>
                              <MenuItem value={100}>100</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={currentPage === 1}
                          onClick={() => handlePageChange(currentPage - 1)}
                          sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                        >
                          Previous
                        </Button>
                        <Typography variant="body2" sx={{ color: 'white', mx: 2 }}>
                          Page {currentPage} of {Math.ceil(totalPages / pageSize)}
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={currentPage >= Math.ceil(totalPages / pageSize)}
                          onClick={() => handlePageChange(currentPage + 1)}
                          sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                        >
                          Next
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            ) : (
              /* List View */
              <Box>
        <DataTable
          customStyles={{
            headCells: {
              style: {
                fontSize: "15px",
                fontWeight: "bold",
                        backgroundColor: 'rgba(0, 161, 221, 0.1)',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        color: '#333'
                      },
                    },
                    rows: {
                      style: {
                        backgroundColor: 'transparent',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        }
                      }
                    }
          }}
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalPages}
          paginationCurrentPage={currentPage}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          paginationPerPage={pageSize}
          paginationRowsPerPageOptions={[10, 20, 50]}
                  onChangeRowsPerPage={(currentRowsPerPage) =>
                    setPageSize(currentRowsPerPage)
                  }
          progressPending={isLoading || !loaderDelay}
          progressComponent={<Loader />}
                  className="league-datatable"
                />
              </Box>
            )}
          </Paper>
        </Stack>
      </Fade>

      {/* Status Change Dialog */}
      <Dialog open={show} onClose={() => setShow(false)} className="league-status-dialog">
        <DialogTitle className="league-status-dialog-title">{warning}</DialogTitle>
        <DialogActions className="league-status-dialog-actions">
          <Button
            variant="outlined"
            onClick={() => {
              toggleChecked(statusId, currentStatus);
              setShow(false);
            }}
            color="primary"
          >
            Yes
          </Button>
          <Button
            variant="outlined"
            onClick={() => setShow(false)}
            color="primary"
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}